import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'rsuite';
import {
  InspectionListFragment,
  InspectionResult,
} from '@/api/ariadne/__generated_types__/graphql';
import { BouncingBoxWithText } from '@/widgets/bouncing-box-loader';
import { AsyncRsGrid } from '@/list/components/async-rs-grid';
import { dayjs } from '@/utils/dayjs';
import {
  INSPECTION_BOOKED,
  InspectionColor,
  InspectionText,
} from './constants';
import { CancelInspectionsModal } from './cancel-inspection-modal';
import styled from 'styled-components';

interface InspectionListRowData {
  data: InspectionListFragment;
}

interface PropertyInspectionsProps {
  propertyAddress: string;
  inspections: InspectionListFragment[];
  onSelectInspection: (inpsection: InspectionListFragment) => void;
  onCancelInspection: (inspection: InspectionListFragment) => void;
  loading: boolean;
}

const CancelRow = ({ data }: InspectionListRowData) => {
  const { inspectionResult } = data;

  if (inspectionResult) {
    return <></>;
  }

  return <Button appearance="ghost">Cancel</Button>;
};

const IssueCountRow = ({ data }: InspectionListRowData) => {
  const inspectionResult = data.inspectionResult as InspectionResult;
  return inspectionResult ? <></> : <></>;
};

const StatusBoxWrapper = styled.div`
  align-items: center;
  display: flex;
`;

const StatusBox = styled.div<{ color?: string }>`
  background-color: color-mix(in srgb, ${(props) => props?.color} 10%, white);
  border-radius: 2px;
  box-sizing: border-box;
  color: ${(props) => props?.color};
  font-size: 12px;
  font-weight: 600;
  height: 22px;
  line-height: 21px;
  padding: 0 6px;
  text-align: center;
  text-transform: capitalize;
`;

const PropertyInspectionsWrapper = styled.div`
  height: 70vh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const InpectionResultRenderer = ({ data }: InspectionListRowData) => {
  const inspectionResult = data.inspectionResult as InspectionResult;
  return (
    <StatusBoxWrapper>
      <StatusBox color={InspectionColor[inspectionResult]}>
        {InspectionText[inspectionResult]}
      </StatusBox>
    </StatusBoxWrapper>
  );
};

const CANCEL_COL_ID = 'cancelCol';

export const PropertyInspections = ({
  inspections,
  propertyAddress,
  loading,
  onSelectInspection,
  onCancelInspection,
}: PropertyInspectionsProps) => {
  const [inspectionToCancel, setInspectionTocancel] =
    useState<InspectionListFragment>();
  const inspectionCols = useMemo(
    () => [
      { headerName: 'Date', field: 'date', flex: 2, sortable: true },
      { headerName: 'Inspector', field: 'inspector', flex: 3 },
      {
        headerName: 'Inspection Result',
        cellRenderer: 'resultCellRenderer',
      },
      /*{ headerName: 'Issue Count', cellRenderer: 'countCellRenderer' },*/
      {
        colId: CANCEL_COL_ID,
        headerName: 'Cancel Inspection',
        editable: false,
        valueGetter: (params: InspectionListFragment) => params.id,
        cellRenderer: 'cancelCellRenderer',
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      flex: 1,
      minWidth: 100,
      filter: false,
      sortable: false,
    }),
    []
  );

  const inspectionRows = useMemo(
    () =>
      !loading
        ? inspections?.map((d) => {
            const date = dayjs.utc(d.inspectionTime).local();
            return {
              ...d,
              date: date.format('MM-DD-YYYY'),
              inspector: d.inspector || INSPECTION_BOOKED,
            };
          })
        : [],
    [inspections, loading]
  );

  const onCellClicked = (value: any) => {
    const colId = value.column.getColId();
    if (CANCEL_COL_ID === colId) {
      if (!value.data.inspectionResult) {
        setInspectionTocancel(value.data);
      }
    } else {
      onSelectInspection(value.data);
    }
  };

  const onCloseCancellation = useCallback(() => {
    setInspectionTocancel(undefined);
  }, []);

  const onAcceptCancellation = useCallback(() => {
    if (inspectionToCancel) {
      onCancelInspection(inspectionToCancel);
      setInspectionTocancel(undefined);
    }
  }, [inspectionToCancel]);

  return (
    <PropertyInspectionsWrapper>
      {loading && <BouncingBoxWithText loadingText="Loading Inspection List" />}
      {!loading && (
        <AsyncRsGrid
          getRowNodeId={(inspection: InspectionListFragment) => inspection.id}
          frameworkComponents={{
            cancelCellRenderer: CancelRow,
            countCellRenderer: IssueCountRow,
            resultCellRenderer: InpectionResultRenderer,
          }}
          columnDefs={inspectionCols}
          rowData={inspectionRows}
          defaultColDef={defaultColDef}
          sideBar={false}
          onCellClicked={onCellClicked}
          rowHeight={50}
        />
      )}
      <CancelInspectionsModal
        onAccept={onAcceptCancellation}
        onClose={onCloseCancellation}
        open={!!inspectionToCancel}
        propertyAddress={propertyAddress}
      />
    </PropertyInspectionsWrapper>
  );
};
